import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import api from '../api';

function Tasks({ user, onLogout }) {
  const location = useLocation();
  const [activeTasks, setActiveTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tasks: []
  });
  const [newTask, setNewTask] = useState({
    assigned_to: [],
    task_description: '',
    due_date: '',
    estimated_hours: ''
  });
  const [error, setError] = useState('');
  const [newTasksCount, setNewTasksCount] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deletedTaskDetail, setDeletedTaskDetail] = useState(null);
  const [showDeletedTaskModal, setShowDeletedTaskModal] = useState(false);
  const [taskMessages, setTaskMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageRecipients, setMessageRecipients] = useState(['everyone']); // Array of recipient IDs
  const [loadingMessages, setLoadingMessages] = useState(false);

  const loadTasks = async () => {
    try {
      const response = await api.getMeetings();
      let allTasks = [];

      // If member, filter to only show tasks assigned to them
      if (user.role === 'member') {
        response.data.forEach(meeting => {
          meeting.tasks
            .filter(task => {
              // Show task if user is in assigned_to OR in members array
              const isAssignedTo = task.assigned_to?._id === user.id;
              const isInMembers = task.members?.some(member => member._id === user.id);
              return isAssignedTo || isInMembers;
            })
            .forEach(task => {
              allTasks.push({
                ...task,
                meetingId: meeting._id,
                meetingTitle: meeting.title,
                meetingDescription: meeting.description
              });
            });
        });
      } else {
        // Directors see all tasks in their lab
        response.data.forEach(meeting => {
          meeting.tasks.forEach(task => {
            allTasks.push({
              ...task,
              meetingId: meeting._id,
              meetingTitle: meeting.title,
              meetingDescription: meeting.description
            });
          });
        });
      }

      // Separate active and completed tasks
      const active = allTasks.filter(t => t.status !== 'completed');
      const completed = allTasks.filter(t => t.status === 'completed');

      setActiveTasks(active);
      setCompletedTasks(completed);

      // Count new tasks (not viewed by the user)
      if (user.role === 'member') {
        const newCount = active.filter(t => !t.is_viewed).length;
        setNewTasksCount(newCount);
      } else {
        // Directors see count of all pending tasks
        const newCount = active.filter(t => t.status === 'pending').length;
        setNewTasksCount(newCount);
      }
    } catch (err) {
      setError('Error loading tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    if (user.role === 'director') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  // Handle navigation from notifications
  useEffect(() => {
    if (location.state?.openTaskId && location.state?.openMeetingId && !loading) {
      // Find the task in activeTasks or completedTasks
      const taskToOpen = [...activeTasks, ...completedTasks].find(
        task => task._id === location.state.openTaskId && task.meetingId === location.state.openMeetingId
      );
      
      if (taskToOpen) {
        handleTaskClick(taskToOpen);
      }
      
      // Clear the state after opening
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, activeTasks, completedTasks, loading]);

  // Handle navigation to deleted tasks from notifications
  useEffect(() => {
    const loadDeletedTask = async () => {
      if (location.state?.openDeletedTaskId && !loading) {
        try {
          const response = await api.getDeletedTask(location.state.openDeletedTaskId);
          setDeletedTaskDetail(response.data);
          setShowDeletedTaskModal(true);
          
          // Mark as viewed
          await api.markDeletedTaskViewed(location.state.openDeletedTaskId);
          
          // Clear the state after opening
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error loading deleted task:', err);
          setError('Failed to load deleted task');
        }
      }
    };
    
    loadDeletedTask();
  }, [location.state, loading]);

  const loadUsers = async () => {
    try {
      const response = await api.getDirectorUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      tasks: []
    });
    setNewTask({
      assigned_to: [],
      task_description: '',
      due_date: '',
      estimated_hours: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await api.updateMeeting(editingId, formData);
      } else {
        await api.createMeeting(formData);
      }
      setShowModal(false);
      resetForm();
      loadTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving tasks');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNewTaskChange = (e) => {
    const { name, value } = e.target;
    setNewTask({
      ...newTask,
      [name]: value
    });
  };

  const addTaskToList = () => {
    if (!newTask.assigned_to || newTask.assigned_to.length === 0 || !newTask.task_description) {
      alert('Please select at least one member and enter a task description');
      return;
    }

    // Create one task with all members assigned
    const task = {
      assigned_to: newTask.assigned_to[0], // First member is main assignee
      members: newTask.assigned_to.slice(1), // Rest are additional members
      task_description: newTask.task_description,
      due_date: newTask.due_date || undefined,
      estimated_hours: newTask.estimated_hours || undefined,
      status: 'pending'
    };

    setFormData({
      ...formData,
      tasks: [...formData.tasks, task]
    });
    setNewTask({
      assigned_to: [],
      task_description: '',
      due_date: '',
      estimated_hours: ''
    });
  };

  const removeTaskFromList = (index) => {
    const updatedTasks = formData.tasks.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      tasks: updatedTasks
    });
  };

  const handleUpdateTaskStatus = async (meetingId, taskId, newStatus) => {
    try {
      await api.updateTaskStatus(meetingId, taskId, newStatus);
      loadTasks();
    } catch (err) {
      setError('Error updating task status');
    }
  };

  const handleMarkViewed = async (meetingId, taskId) => {
    try {
      await api.markTaskViewed(meetingId, taskId);
      loadTasks();
    } catch (err) {
      console.error('Error marking task as viewed:', err);
    }
  };

  const handleTaskClick = (task) => {
    console.log('Opening task:', task);
    console.log('Meeting title:', task.meetingTitle);
    setSelectedTask(task);
    setEditingTask({
      task_description: task.task_description,
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      estimated_hours: task.estimated_hours || '',
      status: task.status,
      meetingTitle: task.meetingTitle || '' // Add meeting title
    });
    console.log('EditingTask set with meetingTitle:', task.meetingTitle);
    setShowTaskModal(true);
    
    // Mark as viewed if member
    if (user.role === 'member' && !task.is_viewed) {
      handleMarkViewed(task.meetingId, task._id);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    console.log('=== HANDLE UPDATE TASK CALLED ===');
    console.log('editingTask:', editingTask);
    console.log('selectedTask:', selectedTask);
    
    if (!editingTask || !selectedTask) {
      console.error('Missing editingTask or selectedTask');
      setError('Error: Missing task data');
      return;
    }
    
    console.log('Updating task with data:', {
      task_description: editingTask.task_description,
      status: editingTask.status,
      due_date: editingTask.due_date,
      estimated_hours: editingTask.estimated_hours,
      meetingTitle: editingTask.meetingTitle
    });
    
    try {
      // Use the new updateTask endpoint for directors
      const response = await api.updateTask(selectedTask.meetingId, selectedTask._id, {
        task_description: editingTask.task_description,
        status: editingTask.status,
        due_date: editingTask.due_date,
        estimated_hours: editingTask.estimated_hours,
        meetingTitle: editingTask.meetingTitle // Include meeting title
      });
      console.log('Task updated successfully, response:', response);
      setShowTaskModal(false);
      setSelectedTask(null);
      setEditingTask(null);
      loadTasks();
    } catch (err) {
      console.error('Error updating task:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || err.message || 'Error updating task';
      setError(errorMsg);
      alert('Error updating task: ' + errorMsg);
    }
  };

  const handleDeleteTask = async (meetingId, taskId) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      setError(''); // Clear any previous errors
      const response = await api.deleteTask(meetingId, taskId);
      console.log('Delete response:', response);
      await loadTasks();
      alert('Task deleted successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error deleting task';
      setError(errorMsg);
      console.error('Delete task error:', err);
      console.error('Error response:', err.response?.data);
      alert(`Failed to delete task: ${errorMsg}`);
    }
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u._id === userId);
    return foundUser ? (foundUser.full_name || foundUser.username) : 'Unknown User';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Load messages for a specific task
  const loadTaskMessages = async (meetingId, taskId) => {
    try {
      setLoadingMessages(true);
      console.log('Loading messages for:', meetingId, taskId);
      const response = await api.getTaskMessages(meetingId, taskId);
      console.log('Messages loaded:', response.data);
      setTaskMessages(response.data);
    } catch (error) {
      console.error('Error loading task messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    console.log('handleSendMessage called');
    console.log('newMessage:', newMessage);
    console.log('selectedTask:', selectedTask);
    console.log('messageRecipients:', messageRecipients);
    
    if (!newMessage.trim() || !selectedTask) {
      console.log('Message not sent - validation failed');
      return;
    }

    try {
      console.log('Sending message...');
      const response = await api.postTaskMessage(selectedTask.meetingId, selectedTask._id, newMessage, messageRecipients);
      console.log('Message sent successfully:', response);
      setNewMessage('');
      setMessageRecipients(['everyone']); // Reset to everyone after sending
      // Reload messages
      await loadTaskMessages(selectedTask.meetingId, selectedTask._id);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response?.data);
      setError('Failed to send message: ' + (error.response?.data?.error || error.message));
      alert('Failed to send message: ' + (error.response?.data?.error || error.message));
    }
  };

  // Add member to task
  const handleAddTaskMember = async (userId) => {
    if (!userId || !selectedTask) return;
    
    try {
      await api.addTaskMember(selectedTask.meetingId, selectedTask._id, userId);
      // Reload tasks to update the members list
      await loadTasks();
      // Update selectedTask with new data
      const response = await api.getMeetings();
      response.data.forEach(meeting => {
        meeting.tasks.forEach(task => {
          if (task._id === selectedTask._id) {
            setSelectedTask({
              ...task,
              meetingId: meeting._id,
              meetingTitle: meeting.title,
              meetingDescription: meeting.description
            });
          }
        });
      });
    } catch (error) {
      console.error('Error adding member:', error);
      setError('Failed to add member: ' + (error.response?.data?.error || error.message));
    }
  };

  // Remove member from task
  const handleRemoveTaskMember = async (userId) => {
    if (!userId || !selectedTask) return;
    
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await api.removeTaskMember(selectedTask.meetingId, selectedTask._id, userId);
      // Reload tasks to update the members list
      await loadTasks();
      // Update selectedTask with new data
      const response = await api.getMeetings();
      response.data.forEach(meeting => {
        meeting.tasks.forEach(task => {
          if (task._id === selectedTask._id) {
            setSelectedTask({
              ...task,
              meetingId: meeting._id,
              meetingTitle: meeting.title,
              meetingDescription: meeting.description
            });
          }
        });
      });
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member: ' + (error.response?.data?.error || error.message));
    }
  };

  // Load messages when task modal opens
  useEffect(() => {
    if (selectedTask && showTaskModal) {
      loadTaskMessages(selectedTask.meetingId, selectedTask._id);
      setMessageRecipients(['everyone']); // Reset recipients when opening a new task
    }
  }, [selectedTask, showTaskModal]);

  const renderTaskCard = (task, isHistory = false) => (
    <div 
      key={task._id} 
      className={`task-card ${!task.is_viewed && user.role === 'member' && !isHistory ? 'task-new' : ''} ${isOverdue(task.due_date) && !isHistory ? 'task-overdue' : ''} ${isHistory ? 'completed-task' : ''}`}
      onClick={() => handleTaskClick(task)}
    >
      <div className="task-header">
        <div className="task-title-section">
          <h4>{task.task_description}</h4>
          <div className="task-badges">
            <span className="task-group-label">{task.meetingTitle}</span>
            <span className={`status-badge status-${task.status}`}>
              {task.status.replace('_', ' ')}
            </span>
            {!task.is_viewed && user.role === 'member' && !isHistory && (
              <span className="new-badge">NEW</span>
            )}
            {task.updated_by && (
              <span className="updated-badge" title="Task has been updated">●</span>
            )}
          </div>
        </div>
        {!isHistory && user.role === 'director' && (
          <div className="task-card-actions">
            <button 
              className="btn btn-primary btn-small"
              onClick={(e) => {
                e.stopPropagation();
                handleTaskClick(task);
              }}
            >
              Edit
            </button>
            <button 
              className="btn btn-danger btn-small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTask(task.meetingId, task._id);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="task-details-grid">
        <div className="task-detail">
          <span className="label">Project Title:</span>
          <span><strong>{task.meetingTitle}</strong></span>
        </div>
        <div className="task-detail">
          <span className="label">Assigned to:</span>
          <span><strong>{task.assigned_to?.full_name || task.assigned_to?.username}</strong></span>
        </div>
        <div className="task-detail">
          <span className="label">Assigned by:</span>
          <span>{task.created_by?.full_name || task.created_by?.username || 'N/A'}</span>
        </div>
        {task.due_date && (
          <div className="task-detail">
            <span className="label">Due date:</span>
            <span className={isOverdue(task.due_date) && !isHistory ? 'overdue-text' : ''}>
              {formatDate(task.due_date)}
              {isOverdue(task.due_date) && !isHistory && ' ⚠️'}
            </span>
          </div>
        )}
        {task.estimated_hours && (
          <div className="task-detail">
            <span className="label">Estimated hours:</span>
            <span>{task.estimated_hours}h</span>
          </div>
        )}
        {task.updated_by && (
          <div className="task-detail">
            <span className="label">Last updated by:</span>
            <span>{task.updated_by.full_name || task.updated_by.username}</span>
          </div>
        )}
        {isHistory && task.completed_at && (
          <div className="task-detail">
            <span className="label">Completed:</span>
            <span>{formatDateTime(task.completed_at)}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} newTasksCount={newTasksCount} />
      
      <div className="main-content">
        <div className="page-header-modern">
          <div className="page-title-section">
            <h1>Tasks</h1>
            <p>{user.role === 'director' ? 'Assign and manage tasks for your team' : 'View and update your assigned tasks'}</p>
          </div>
          <div className="page-actions">
            <button 
              className={`btn-modern ${showHistory ? 'btn-modern-secondary' : 'btn-modern-primary'}`}
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? '📋 Active Tasks' : '✓ History'}
            </button>
            {user.role === 'director' && !showHistory && (
              <button className="btn-modern btn-modern-success" onClick={handleAdd}>
                + New Task Group
              </button>
            )}
          </div>
        </div>

        <div className="tasks-container-modern">
          <div className="tasks-header-modern">
            <h2 className="tasks-title-modern">
              {showHistory ? '✓ Completed Tasks' : '📋 Active Tasks'}
              <span className="task-count-badge">
                {showHistory ? completedTasks.length : activeTasks.length}
              </span>
            </h2>
          </div>

          {loading ? (
            <div className="loading-state-modern">
              <div className="loading-spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : showHistory ? (
            completedTasks.length === 0 ? (
              <div className="empty-state-modern">
                <div className="empty-icon">✓</div>
                <h3>No completed tasks yet</h3>
                <p>Completed tasks will appear here</p>
              </div>
            ) : (
              <div className="tasks-grid-modern">
                {completedTasks.map((task) => renderTaskCard(task, true))}
              </div>
            )
          ) : (
            activeTasks.length === 0 ? (
              <div className="empty-state-modern">
                <div className="empty-icon">📋</div>
                <h3>{user.role === 'director' ? 'No task groups yet' : 'No tasks assigned'}</h3>
                <p>{user.role === 'director' ? 'Create your first task group to get started' : 'You have no active tasks at the moment'}</p>
                {user.role === 'director' && (
                  <button className="btn-modern btn-modern-primary" onClick={handleAdd}>
                    + Create Task Group
                  </button>
                )}
              </div>
            ) : (
              <div className="tasks-grid-modern">
                {activeTasks.map((task) => renderTaskCard(task, false))}
              </div>
            )
          )}
        </div>

        {showModal && user.role === 'director' && (
          <div className="modal-overlay">
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>{editingId ? 'Edit Task Group' : 'Create Task Group'}</h2>
                  <p style={{ fontSize: '0.875rem', color: '#65676b', margin: '0.25rem 0 0 0' }}>
                    {editingId ? 'Update your project and tasks' : 'Create a new project with tasks'}
                  </p>
                </div>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="error-message">{error}</div>}
                  
                  <div className="form-group">
                    <label>Project Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter project title"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Optional project description..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Tasks *</label>
                    {formData.tasks.length === 0 && (
                      <p style={{ fontSize: '0.875rem', color: '#ef4444', marginBottom: '0.5rem', fontWeight: '500' }}>
                        ⚠ Add at least one task to create a task group
                      </p>
                    )}
                    <div className="task-builder">
                      {/* Task Members Selection */}
                      <div className="form-group">
                        <label style={{ fontSize: '0.875rem', marginBottom: '10px', display: 'block', fontWeight: '600' }}>
                          👥 Assign Members to Task
                        </label>
                        
                        {/* Display selected members */}
                        <div className="members-list" style={{ marginBottom: '15px' }}>
                          {newTask.assigned_to.map((userId) => {
                            const user = users.find(u => u._id === userId);
                            if (!user) return null;
                            return (
                              <div key={userId} style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                border: '1px solid #ddd'
                              }}>
                                <div style={{
                                  width: '35px',
                                  height: '35px',
                                  borderRadius: '50%',
                                  backgroundColor: user.role === 'director' ? '#0084ff' : '#6c757d',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  marginRight: '12px',
                                  fontSize: '0.9rem'
                                }}>
                                  {(user.full_name || user.username).charAt(0).toUpperCase()}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 'bold', color: '#111', fontSize: '0.95rem' }}>
                                    {user.full_name || user.username}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                    {user.role === 'director' ? 'Director' : 'Member'}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewTask({
                                      ...newTask,
                                      assigned_to: newTask.assigned_to.filter(id => id !== userId)
                                    });
                                  }}
                                  style={{
                                    backgroundColor: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '5px 10px',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                          
                          {newTask.assigned_to.length === 0 && (
                            <div style={{ 
                              padding: '15px', 
                              backgroundColor: '#fff3cd', 
                              borderRadius: '8px',
                              color: '#856404',
                              fontSize: '0.875rem',
                              marginBottom: '10px'
                            }}>
                              No members assigned yet. Add members below.
                            </div>
                          )}
                        </div>
                        
                        {/* Add member dropdown */}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.85rem', marginBottom: '5px', display: 'block' }}>
                              Add Member:
                            </label>
                            <select
                              value=""
                              onChange={(e) => {
                                const userId = e.target.value;
                                if (userId && !newTask.assigned_to.includes(userId)) {
                                  setNewTask({
                                    ...newTask,
                                    assigned_to: [...newTask.assigned_to, userId]
                                  });
                                }
                                e.target.value = ''; // Reset dropdown
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: '0.95rem'
                              }}
                            >
                              <option value="">Select a member to add...</option>
                              {users
                                .filter(u => !newTask.assigned_to.includes(u._id))
                                .map(u => (
                                  <option key={u._id} value={u._id}>
                                    {u.full_name || u.username} ({u.role === 'director' ? 'Director' : 'Member'})
                                  </option>
                                ))
                              }
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Task Description *</label>
                        <textarea
                          name="task_description"
                          value={newTask.task_description}
                          onChange={handleNewTaskChange}
                          placeholder="Enter task description..."
                          rows="3"
                          className="form-control"
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label style={{ fontSize: '0.875rem' }}>Due Date:</label>
                          <input
                            type="date"
                            name="due_date"
                            value={newTask.due_date}
                            onChange={handleNewTaskChange}
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label style={{ fontSize: '0.875rem' }}>Estimated Hours:</label>
                          <input
                            type="number"
                            name="estimated_hours"
                            value={newTask.estimated_hours}
                            onChange={handleNewTaskChange}
                            placeholder="e.g., 5"
                            className="form-control"
                            min="0"
                            step="0.5"
                          />
                        </div>
                      </div>
                      
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={addTaskToList}
                        style={{ marginTop: '10px', width: '100%' }}
                      >
                        Add Task to List
                      </button>
                    </div>

                    {formData.tasks.length > 0 && (
                      <div className="tasks-preview" style={{ marginTop: '15px' }}>
                        <h4>Tasks to be assigned: ({formData.tasks.length})</h4>
                        {formData.tasks.map((task, index) => {
                          // Get all member names
                          const mainAssignee = getUserName(task.assigned_to);
                          const additionalMembers = task.members && task.members.length > 0
                            ? task.members.map(memberId => getUserName(memberId)).join(', ')
                            : '';
                          
                          return (
                            <div key={index} className="task-preview-item">
                              <div>
                                <strong>
                                  {mainAssignee}
                                  {additionalMembers && ` + ${additionalMembers}`}
                                </strong>
                                <p>{task.task_description}</p>
                                {(task.due_date || task.estimated_hours) && (
                                  <small style={{ color: '#6b7280' }}>
                                    {task.due_date && `Due: ${formatDate(task.due_date)}`}
                                    {task.due_date && task.estimated_hours && ' • '}
                                    {task.estimated_hours && `${task.estimated_hours}h`}
                                  </small>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-danger btn-small"
                                onClick={() => removeTaskFromList(index)}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={formData.tasks.length === 0}
                  >
                    {editingId ? 'Update' : 'OK'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Task Detail/Edit Modal */}
        {showTaskModal && selectedTask && (
          <div className="modal-overlay">
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>Task Details</h2>
                  <p style={{ fontSize: '0.875rem', color: '#65676b', margin: '0.25rem 0 0 0' }}>
                    Project: <strong>{user.role === 'director' && editingTask ? editingTask.meetingTitle : selectedTask.meetingTitle}</strong>
                  </p>
                </div>
                <button className="close-btn" onClick={() => setShowTaskModal(false)}>×</button>
              </div>
              
              {user.role === 'director' ? (
                <form onSubmit={handleUpdateTask}>
                  <div className="modal-body">
                    {error && <div className="error-message">{error}</div>}
                    
                    <div className="form-group">
                      <label>Project Title *</label>
                      <input
                        type="text"
                        value={editingTask?.meetingTitle || ''}
                        onChange={(e) => setEditingTask({...editingTask, meetingTitle: e.target.value})}
                        required
                        placeholder="Enter project title"
                      />
                    </div>

                    <div className="form-group">
                      <label>Task Description *</label>
                      <textarea
                        value={editingTask?.task_description || ''}
                        onChange={(e) => setEditingTask({...editingTask, task_description: e.target.value})}
                        rows="4"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={editingTask?.status || 'pending'}
                          onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Estimated Hours</label>
                        <input
                          type="number"
                          value={editingTask?.estimated_hours || ''}
                          onChange={(e) => setEditingTask({...editingTask, estimated_hours: e.target.value})}
                          min="0"
                          step="0.5"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Due Date</label>
                      <input
                        type="date"
                        value={editingTask?.due_date || ''}
                        onChange={(e) => setEditingTask({...editingTask, due_date: e.target.value})}
                      />
                    </div>

                    {/* Task Members Management Section */}
                    <div className="task-members-management" style={{ marginTop: '25px' }}>
                      <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#111' }}>👥 Task Members</h3>
                      
                      <div className="members-list" style={{ marginBottom: '15px' }}>
                        {/* Main Assigned Person */}
                        {selectedTask.assigned_to && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px',
                            backgroundColor: '#e7f3ff',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            border: '2px solid #0084ff'
                          }}>
                            <div style={{
                              width: '35px',
                              height: '35px',
                              borderRadius: '50%',
                              backgroundColor: '#0084ff',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              marginRight: '12px'
                            }}>
                              {(selectedTask.assigned_to.full_name || selectedTask.assigned_to.username).charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', color: '#111' }}>
                                {selectedTask.assigned_to.full_name || selectedTask.assigned_to.username}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>Main Assignee</div>
                            </div>
                          </div>
                        )}

                        {/* Additional Members */}
                        {selectedTask.members && selectedTask.members.length > 0 && selectedTask.members.map((member) => (
                          <div key={member._id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            border: '1px solid #ddd'
                          }}>
                            <div style={{
                              width: '35px',
                              height: '35px',
                              borderRadius: '50%',
                              backgroundColor: '#6c757d',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              marginRight: '12px'
                            }}>
                              {(member.full_name || member.username).charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', color: '#111' }}>
                                {member.full_name || member.username}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>Member</div>
                            </div>
                            {user.role === 'director' && (
                              <button
                                type="button"
                                onClick={() => handleRemoveTaskMember(member._id)}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '5px 10px',
                                  fontSize: '0.8rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add Member Section (Directors Only) */}
                      {user.role === 'director' && (
                        <div style={{ marginTop: '15px' }}>
                          <select 
                            className="form-control" 
                            style={{ 
                              width: '100%',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ddd',
                              fontSize: '0.9rem'
                            }}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAddTaskMember(e.target.value);
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">+ Add member or director to this task</option>
                            {users
                              .filter(u => {
                                // Exclude main assignee
                                if (u._id === selectedTask.assigned_to?._id) return false;
                                // Exclude already added members
                                if (selectedTask.members && selectedTask.members.some(m => m._id === u._id)) return false;
                                return true;
                              })
                              .map(u => (
                                <option key={u._id} value={u._id}>
                                  {u.full_name || u.username} {u.role === 'director' ? '(Director)' : '(Member)'}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="task-history-section" style={{ marginTop: '25px' }}>
                      <h3 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>📋 Modification History</h3>
                      {selectedTask.modification_history && selectedTask.modification_history.length > 0 ? (
                        <div className="modification-timeline">
                          {selectedTask.modification_history.map((history, index) => (
                            <div key={index} className="timeline-item">
                              <div className="timeline-marker"></div>
                              <div className="timeline-content">
                                <div className="timeline-header">
                                  <strong>{history.modified_by?.full_name || history.modified_by?.username || 'Unknown'}</strong>
                                  <span className="timeline-action">
                                    {history.action === 'created' && 'created this task'}
                                    {history.action === 'status_changed' && 'changed status'}
                                    {history.action === 'task_updated' && 'updated task'}
                                    {history.action === 'completed' && 'marked as completed'}
                                  </span>
                                </div>
                                <div className="timeline-time">{formatDateTime(history.modified_at)}</div>
                                {history.changes && Object.keys(history.changes).length > 0 && (
                                  <div className="timeline-changes">
                                    {Object.entries(history.changes).map(([key, value]) => (
                                      <div key={key} className="change-item">
                                        <span className="change-label">{key.replace('_', ' ')}:</span>
                                        <span className="change-value">
                                          {key.includes('date') ? formatDate(value) : 
                                           key === 'status' ? value.replace('_', ' ') :
                                           value?.toString() || 'N/A'}
                                        </span>
                                        {history.previous_values && history.previous_values[key] && (
                                          <span className="change-previous">
                                            (was: {key.includes('date') ? formatDate(history.previous_values[key]) : 
                                                   key === 'status' ? history.previous_values[key].replace('_', ' ') :
                                                   history.previous_values[key]?.toString()})
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-history">No modification history available</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="modal-footer">
                    <button type="submit" className="btn btn-primary">
                      Save Changes
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={() => {
                        setShowTaskModal(false);
                        handleDeleteTask(selectedTask.meetingId, selectedTask._id);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      Delete Task
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowTaskModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="modal-body">
                    <div className="task-detail-view">
                      <div className="detail-section">
                        <h3>Task Description</h3>
                        <p>{selectedTask.task_description}</p>
                      </div>

                      <div className="detail-row">
                        <div className="detail-item">
                          <span className="label">Status:</span>
                          <span className={`status-badge status-${selectedTask.status}`}>
                            {selectedTask.status.replace('_', ' ')}
                          </span>
                        </div>
                        {selectedTask.due_date && (
                          <div className="detail-item">
                            <span className="label">Due Date:</span>
                            <span className={isOverdue(selectedTask.due_date) && selectedTask.status !== 'completed' ? 'overdue-text' : ''}>
                              {formatDate(selectedTask.due_date)}
                              {isOverdue(selectedTask.due_date) && selectedTask.status !== 'completed' && ' ⚠️'}
                            </span>
                          </div>
                        )}
                        {selectedTask.estimated_hours && (
                          <div className="detail-item">
                            <span className="label">Estimated Hours:</span>
                            <span>{selectedTask.estimated_hours}h</span>
                          </div>
                        )}
                      </div>

                      {user.role === 'member' && selectedTask.status !== 'completed' && (
                        <div className="status-update-section">
                          <label>Update Status:</label>
                          <select
                            className={`task-status-select status-${selectedTask.status}`}
                            value={selectedTask.status}
                            onChange={(e) => {
                              handleUpdateTaskStatus(selectedTask.meetingId, selectedTask._id, e.target.value);
                              setShowTaskModal(false);
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                      )}

                      {/* Task Members Display Section */}
                      <div className="task-members-display" style={{ marginTop: '25px' }}>
                        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#111' }}>👥 Task Members</h3>
                        
                        <div className="members-list" style={{ marginBottom: '15px' }}>
                          {/* Main Assigned Person */}
                          {selectedTask.assigned_to && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '10px',
                              backgroundColor: '#e7f3ff',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              border: '2px solid #0084ff'
                            }}>
                              <div style={{
                                width: '35px',
                                height: '35px',
                                borderRadius: '50%',
                                backgroundColor: '#0084ff',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                marginRight: '12px'
                              }}>
                                {(selectedTask.assigned_to.full_name || selectedTask.assigned_to.username).charAt(0).toUpperCase()}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', color: '#111' }}>
                                  {selectedTask.assigned_to.full_name || selectedTask.assigned_to.username}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>Main Assignee</div>
                              </div>
                            </div>
                          )}

                          {/* Additional Members */}
                          {selectedTask.members && selectedTask.members.length > 0 && selectedTask.members.map((member) => (
                            <div key={member._id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '10px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              border: '1px solid #ddd'
                            }}>
                              <div style={{
                                width: '35px',
                                height: '35px',
                                borderRadius: '50%',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                marginRight: '12px'
                              }}>
                                {(member.full_name || member.username).charAt(0).toUpperCase()}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', color: '#111' }}>
                                  {member.full_name || member.username}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>Member</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="task-history-section" style={{ marginTop: '25px' }}>
                        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>📋 Modification History</h3>
                        {selectedTask.modification_history && selectedTask.modification_history.length > 0 ? (
                          <div className="modification-timeline">
                            {selectedTask.modification_history.map((history, index) => (
                              <div key={index} className="timeline-item">
                                <div className="timeline-marker"></div>
                                <div className="timeline-content">
                                  <div className="timeline-header">
                                    <strong>{history.modified_by?.full_name || history.modified_by?.username || 'Unknown'}</strong>
                                    <span className="timeline-action">
                                      {history.action === 'created' && 'created this task'}
                                      {history.action === 'status_changed' && 'changed status'}
                                      {history.action === 'task_updated' && 'updated task'}
                                      {history.action === 'completed' && 'marked as completed'}
                                    </span>
                                  </div>
                                  <div className="timeline-time">{formatDateTime(history.modified_at)}</div>
                                  {history.changes && Object.keys(history.changes).length > 0 && (
                                    <div className="timeline-changes">
                                      {Object.entries(history.changes).map(([key, value]) => (
                                        <div key={key} className="change-item">
                                          <span className="change-label">{key.replace('_', ' ')}:</span>
                                          <span className="change-value">
                                            {key.includes('date') ? formatDate(value) : 
                                             key === 'status' ? value.replace('_', ' ') :
                                             value?.toString() || 'N/A'}
                                          </span>
                                          {history.previous_values && history.previous_values[key] && (
                                            <span className="change-previous">
                                              (was: {key.includes('date') ? formatDate(history.previous_values[key]) : 
                                                     key === 'status' ? history.previous_values[key].replace('_', ' ') :
                                                     history.previous_values[key]?.toString()})
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-history">No modification history available</p>
                        )}
                      </div>

                      {/* Task Members Section (Directors Only) */}
                      {user.role === 'director' && (
                        <div className="task-members-section" style={{ marginTop: '25px' }}>
                          <h3>� Task Members</h3>
                          <div className="members-list" style={{ marginBottom: '15px' }}>
                            {selectedTask.assigned_to && (
                              <div className="member-badge" style={{
                                display: 'inline-block',
                                padding: '8px 12px',
                                backgroundColor: '#e7f3ff',
                                borderRadius: '20px',
                                margin: '5px',
                                fontSize: '0.9rem'
                              }}>
                                <span style={{ fontWeight: 'bold' }}>
                                  {selectedTask.assigned_to.full_name || selectedTask.assigned_to.username}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="add-member-form" style={{ marginTop: '10px' }}>
                            <select 
                              className="form-control" 
                              style={{ width: 'auto', display: 'inline-block', marginRight: '10px' }}
                              onChange={(e) => {
                                if (e.target.value) {
                                  // TODO: Add member to task
                                  console.log('Add member:', e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">+ Add member</option>
                              {users
                                .filter(u => u.role === 'member' && u._id !== selectedTask.assigned_to?._id)
                                .map(u => (
                                  <option key={u._id} value={u._id}>
                                    {u.full_name || u.username}
                                  </option>
                                ))
                              }
                            </select>
                          </div>
                        </div>
                      )}

                      {/* WhatsApp-Style Chat Section */}
                      <div className="whatsapp-chat-section" style={{ marginTop: '25px' }}>
                        <h3 style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          marginBottom: '15px',
                          color: '#111'
                        }}>💬 Comments</h3>
                        
                        <div className="whatsapp-messages" style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          backgroundColor: '#e5ddd5',
                          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d9d9d9\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z\'/%3E%3C/g%3E%3C/svg%3E")',
                          padding: '15px',
                          borderRadius: '8px',
                          marginBottom: '10px'
                        }}>
                          {loadingMessages ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#667' }}>
                              Loading messages...
                            </div>
                          ) : taskMessages.length > 0 ? (
                            taskMessages.map((msg, index) => (
                              <div key={index} style={{
                                display: 'flex',
                                justifyContent: msg.sender._id === user.id ? 'flex-end' : 'flex-start',
                                marginBottom: '8px'
                              }}>
                                <div style={{
                                  maxWidth: '65%',
                                  backgroundColor: msg.sender._id === user.id ? '#dcf8c6' : '#fff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                  position: 'relative'
                                }}>
                                  {msg.sender._id !== user.id && (
                                    <div style={{
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold',
                                      color: '#25d366',
                                      marginBottom: '4px'
                                    }}>
                                      {msg.sender.full_name || msg.sender.username}
                                    </div>
                                  )}
                                  <div style={{
                                    fontSize: '0.9rem',
                                    color: '#000',
                                    wordBreak: 'break-word',
                                    marginBottom: '4px'
                                  }}>
                                    {msg.message}
                                  </div>
                                  <div style={{
                                    fontSize: '0.7rem',
                                    color: '#667',
                                    textAlign: 'right'
                                  }}>
                                    {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '40px 20px', 
                              color: '#667',
                              backgroundColor: '#fff',
                              borderRadius: '8px'
                            }}>
                              No messages yet. Start the conversation!
                            </div>
                          )}
                        </div>
                        
                        <form onSubmit={handleSendMessage} style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center'
                        }}>
                          <input
                            type="text"
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            style={{
                              flex: 1,
                              padding: '10px 15px',
                              border: '1px solid #ddd',
                              borderRadius: '24px',
                              fontSize: '0.9rem',
                              outline: 'none'
                            }}
                          />
                          <button 
                            type="submit" 
                            disabled={!newMessage.trim()}
                            style={{
                              backgroundColor: '#25d366',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '45px',
                              height: '45px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                              opacity: newMessage.trim() ? 1 : 0.5,
                              transition: 'all 0.2s'
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="22" y1="2" x2="11" y2="13"></line>
                              <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor"></polygon>
                            </svg>
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                  
                  <div className="modal-footer">
                    {user.role === 'director' && (
                      <button 
                        type="button" 
                        className="btn btn-danger"
                        onClick={() => {
                          setShowTaskModal(false);
                          handleDeleteTask(selectedTask.meetingId, selectedTask._id);
                        }}
                      >
                        Delete Task
                      </button>
                    )}
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowTaskModal(false)}
                      style={{ marginLeft: user.role === 'director' ? '10px' : 'auto' }}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Deleted Task Modal - Read Only */}
        {showDeletedTaskModal && deletedTaskDetail && (
          <div className="modal-overlay">
            <div className="modal-content modal-large">
              <div className="modal-header">
                <h2>Deleted Task (Read Only)</h2>
                <button className="modal-close" onClick={() => setShowDeletedTaskModal(false)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="deletion-notice" style={{
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #EF4444',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '20px'
                }}>
                  <p style={{ margin: 0, color: '#991B1B', fontWeight: 'bold' }}>
                    🗑️ This task was deleted by {deletedTaskDetail.deleted_by?.full_name || deletedTaskDetail.deleted_by?.username || 'Unknown'} on {formatDateTime(deletedTaskDetail.deleted_at)}
                  </p>
                  <p style={{ margin: '5px 0 0 0', color: '#7F1D1D', fontSize: '0.9em' }}>
                    You can view the information below but cannot modify it.
                  </p>
                </div>

                <div className="task-details">
                  <div className="task-detail">
                    <span className="label">Task Description:</span>
                    <span>{deletedTaskDetail.task_description}</span>
                  </div>
                  
                  <div className="task-detail">
                    <span className="label">Status:</span>
                    <span className={`status-badge status-${deletedTaskDetail.status}`}>
                      {deletedTaskDetail.status?.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="task-detail">
                    <span className="label">Assigned By:</span>
                    <span>
                      {deletedTaskDetail.created_by?.full_name || deletedTaskDetail.created_by?.username || 'Unknown'}
                    </span>
                  </div>

                  <div className="task-detail">
                    <span className="label">Assigned To:</span>
                    <span>
                      {deletedTaskDetail.assigned_to?.full_name || deletedTaskDetail.assigned_to?.username || 'Unknown'}
                    </span>
                  </div>

                  {deletedTaskDetail.due_date && (
                    <div className="task-detail">
                      <span className="label">Due Date:</span>
                      <span>{formatDate(deletedTaskDetail.due_date)}</span>
                    </div>
                  )}

                  {deletedTaskDetail.estimated_hours && (
                    <div className="task-detail">
                      <span className="label">Estimated Hours:</span>
                      <span>{deletedTaskDetail.estimated_hours}h</span>
                    </div>
                  )}

                  {deletedTaskDetail.completed_at && (
                    <div className="task-detail">
                      <span className="label">Completed At:</span>
                      <span>{formatDateTime(deletedTaskDetail.completed_at)}</span>
                    </div>
                  )}
                </div>

                <div className="task-history-section">
                  <h3>Task Group Information</h3>
                  <div className="history-grid">
                    <div className="history-item">
                      <span className="history-label">Task Group:</span>
                      <span>{deletedTaskDetail.meeting_title}</span>
                    </div>
                    {deletedTaskDetail.meeting_description && (
                      <div className="history-item full-width">
                        <span className="history-label">Group Description:</span>
                        <span>{deletedTaskDetail.meeting_description}</span>
                      </div>
                    )}
                  </div>

                  <h3 style={{ marginTop: '25px' }}>Modification History</h3>
                  {deletedTaskDetail.modification_history && deletedTaskDetail.modification_history.length > 0 ? (
                    <div className="modification-timeline">
                      {deletedTaskDetail.modification_history.map((history, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-marker"></div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <strong>{history.modified_by?.full_name || history.modified_by?.username || 'Unknown'}</strong>
                              <span className="timeline-action">
                                {history.action === 'created' && 'created this task'}
                                {history.action === 'status_changed' && 'changed status'}
                                {history.action === 'task_updated' && 'updated task'}
                                {history.action === 'completed' && 'marked as completed'}
                              </span>
                            </div>
                            <div className="timeline-time">{formatDateTime(history.modified_at)}</div>
                            {history.changes && Object.keys(history.changes).length > 0 && (
                              <div className="timeline-changes">
                                {Object.entries(history.changes).map(([key, value]) => (
                                  <div key={key} className="change-item">
                                    <span className="change-label">{key.replace('_', ' ')}:</span>
                                    <span className="change-value">
                                      {key.includes('date') ? formatDate(value) : 
                                       key === 'status' ? value.replace('_', ' ') :
                                       value?.toString() || 'N/A'}
                                    </span>
                                    {history.previous_values && history.previous_values[key] && (
                                      <span className="change-previous">
                                        (was: {key.includes('date') ? formatDate(history.previous_values[key]) : 
                                               key === 'status' ? history.previous_values[key].replace('_', ' ') :
                                               history.previous_values[key]?.toString()})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="timeline-item">
                        <div className="timeline-marker deletion-marker" style={{ backgroundColor: '#EF4444' }}></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <strong>{deletedTaskDetail.deleted_by?.full_name || deletedTaskDetail.deleted_by?.username || 'Unknown'}</strong>
                            <span className="timeline-action" style={{ color: '#EF4444' }}>deleted this task</span>
                          </div>
                          <div className="timeline-time">{formatDateTime(deletedTaskDetail.deleted_at)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="no-history">No modification history available</p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowDeletedTaskModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Tasks;
